import React, { useCallback, useEffect, useRef, useState } from 'react';
import { request, fmt, getSessionUser, returnTo } from '../api.js';
import { Login } from './auth.jsx';

const textOf = c => (typeof c === 'string' ? c : (c || []).map(b => (b.type === 'text' ? b.text : b.type === 'tool_use' ? `⏺ ${b.name}(${JSON.stringify(b.input).slice(0, 160)})` : b.type === 'tool_result' ? `  ⎿ ${typeof b.content === 'string' ? b.content.slice(0, 400) : '(result)'}` : '')).filter(Boolean).join('\n'));

const AUDIENCE = { owner: 'Private', users: 'Shared with specific people', public: 'Public' };

// /a/:slug: an artifact published from Forge Code. The page runs in a sandboxed frame with an opaque origin, so it
// cannot reach the console's session. Private and shared artifacts ask the visitor to sign in first.
export function ArtifactView({ slug }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [needLogin, setNeedLogin] = useState(false);
  const load = useCallback(async () => {
    setError(null);
    try { setData(await request(`/v1/artifacts/${slug}/content`)); setNeedLogin(false); } catch (e) {
      if (e.status === 401 || (e.status === 404 && !(await getSessionUser()))) { returnTo.set(window.location.pathname); setNeedLogin(true); } else setError(e.message);
    }
  }, [slug]);
  useEffect(() => { load(); }, [load]);
  if (needLogin) return <Login onSignedIn={load} />;
  return (
    <div className="artifact-view">
      <div className="artifact-bar">
        <span className="brand"><img src="/icon.svg" alt="" />Forge</span>
        {data && <><strong className="artifact-title">{data.title}</strong><span className="muted">{AUDIENCE[data.audience]} · updated {fmt.date(data.updated_at)}</span></>}
      </div>
      {error && <div className="error" style={{ margin: 16 }}>{error}</div>}
      {data && <iframe className="artifact-frame" title={data.title} sandbox="allow-scripts allow-popups allow-forms allow-modals allow-downloads" srcDoc={data.html} />}
    </div>
  );
}

// /s/:id: a conversation shared with /share. Public: the link is the secret.
export function ShareView({ id }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => { request(`/v1/public/shares/${id}`, { anonymous: true }).then(setData, e => setError(e.message)); }, [id]);
  return (
    <div className="main" style={{ margin: '0 auto', maxWidth: 860 }}>
      <div className="brand" style={{ padding: '0 0 12px' }}><img src="/icon.svg" alt="" width="24" />Forge Code · shared conversation</div>
      {error && <div className="error">{error}</div>}
      {data && <>
        <h1>{data.title}</h1>
        <p className="muted">Shared {fmt.date(data.created_at)}{data.transcript.model ? ` · ${data.transcript.model}` : ''}</p>
        <div className="chat">
          {data.transcript.messages.map((m, i) => {
            const t = textOf(m.content);
            if (!t.trim()) return null;
            const tool = Array.isArray(m.content) && m.content.every(b => b.type === 'tool_result');
            return <div key={i} className={`msg ${m.role} ${tool ? 'tool' : ''}`}><div className="who">{tool ? 'tool result' : m.role === 'user' ? 'You' : 'Forge'}</div>{t}</div>;
          })}
        </div>
      </>}
    </div>
  );
}

// /remote/:room#token=…: watch a Forge Code session live and send it prompts (Cloudflare Durable Object relay).
export function Remote({ room }) {
  const [items, setItems] = useState([]);
  const [state, setState] = useState('connecting');
  const [presence, setPresence] = useState(null);
  const [busy, setBusy] = useState(false);
  const [text, setText] = useState('');
  const [error, setError] = useState(null);
  const ws = useRef(null);
  const end = useRef(null);
  useEffect(() => {
    let closed = false;
    (async () => {
      let token = new URLSearchParams(window.location.hash.slice(1)).get('token');
      let relay = null;
      try { const r = await request(`/v1/remote/${room}/token`); token = r.token; relay = r.relay_url; } catch (e) { if (!token) { setError(e.message); setState('error'); return; } }
      relay ??= (await request('/v1/config', { anonymous: true })).relay_url;
      const sock = new WebSocket(`${relay}/rooms/${room}/view?token=${encodeURIComponent(token)}`);
      ws.current = sock;
      sock.onopen = () => setState('connected');
      sock.onclose = () => { if (!closed) setState('disconnected'); };
      sock.onmessage = e => {
        const m = JSON.parse(e.data);
        if (m.type === 'history') setItems(m.events.filter(x => x.type === 'item').map(x => x.item));
        else if (m.type === 'item') setItems(l => [...l, m.item]);
        else if (m.type === 'status') setBusy(!!m.busy);
        else if (m.type === 'presence') setPresence(m);
        else if (m.type === 'closed') setState('ended');
        else if (m.type === 'error') setError(m.message);
      };
    })();
    return () => { closed = true; ws.current?.close(); };
  }, [room]);
  useEffect(() => { end.current?.scrollIntoView({ block: 'end' }); }, [items]);
  const send = e => {
    e.preventDefault();
    if (!text.trim()) return;
    setError(null);
    ws.current?.send(JSON.stringify({ type: 'prompt', text }));
    setText('');
  };
  return (
    <div className="main" style={{ margin: '0 auto', maxWidth: 860 }}>
      <div className="page-head">
        <div><div className="brand" style={{ padding: 0 }}><img src="/icon.svg" alt="" width="24" />Remote Control</div><p className="muted mono">room {room}</p></div>
        <div className="row"><span className={`pill ${state === 'connected' ? 'ok' : state === 'ended' ? '' : 'warn'}`}>{state}</span>{presence && <span className="pill">{presence.host ? 'Forge Code online' : 'Forge Code offline'}</span>}{busy && <span className="pill accent">working…</span>}</div>
      </div>
      {error && <div className="error">{error}</div>}
      <div className="chat">
        {items.map((it, i) => (
          <div key={i} className={`msg ${it.kind === 'user' ? 'user' : it.kind === 'tool' ? 'tool' : ''}`}>
            <div className="who">{it.kind === 'user' ? 'You' : it.kind === 'assistant' ? 'Forge' : it.kind}</div>
            {(it.tool ? `⏺ ${it.tool.name} ${it.tool.input}${it.summary ? `\n  ⎿ ${it.summary}` : ''}` : it.text || it.summary || it.prefix || '').replace(/\x1b\[[0-9;]*m/g, '')}
          </div>
        ))}
        {!items.length && <div className="card muted">Waiting for the session's first message…</div>}
        <div ref={end} />
      </div>
      {state !== 'ended' && <form className="composer" onSubmit={send}><input value={text} onChange={e => setText(e.target.value)} placeholder="Send a prompt to this session" aria-label="Prompt" /><button className="btn primary" disabled={state !== 'connected'}>Send</button></form>}
    </div>
  );
}
